import React, { useContext, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { CollectionClientDetail } from '../model';
import { useUpdateCollectionClientBackupEmail } from '../hooks/useUpdateCollectionClientBackupEmail';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface Props {
  client: CollectionClientDetail;
  onSaved: () => void;
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '';

const CollectionClientBackupEmailCard: React.FC<Props> = ({ client, onSaved }) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const { updateBackupEmail, isLoading } = useUpdateCollectionClientBackupEmail();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(client.backup_email ?? '');

  const hasServmanEmail = client.servman_emails.length > 0;
  const hasBackup = !!client.backup_email;

  if (hasServmanEmail && !hasBackup) return null;

  const beginEdit = () => {
    setDraft(client.backup_email ?? '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(client.backup_email ?? '');
    setEditing(false);
  };

  const handleSave = async (value: string | null) => {
    const clientId = client.collections_client_id;
    if (clientId == null) return;

    try {
      await updateBackupEmail({ id: clientId, backup_email: value, location: client.location });
      showSuccessSnackBar?.(value ? 'Backup email saved.' : 'Backup email cleared.');
      setEditing(false);
      onSaved();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: string[]; error?: string } } };
      const message =
        err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'Failed to update backup email.';
      showErrorSnackBar?.(message);
    }
  };

  if (!hasBackup) {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="error" sx={{ mb: 1 }}>
          No company email on file. Add a backup email so collection emails can reach this client.
        </Alert>
        {editing ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Backup email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={isLoading}
            />
            <IconButton
              onClick={() => handleSave(draft.trim() || null)}
              disabled={isLoading || !draft.trim()}
              aria-label="Save backup email"
            >
              <SaveIcon />
            </IconButton>
            <IconButton onClick={cancelEdit} disabled={isLoading} aria-label="Cancel">
              <CloseIcon />
            </IconButton>
          </Box>
        ) : (
          <Button variant="outlined" size="small" onClick={beginEdit}>
            Add backup email
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {editing ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="Backup email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={isLoading}
          />
          <IconButton
            onClick={() => handleSave(draft.trim() || null)}
            disabled={isLoading || !draft.trim()}
            aria-label="Save backup email"
          >
            <SaveIcon />
          </IconButton>
          <IconButton onClick={cancelEdit} disabled={isLoading} aria-label="Cancel">
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Backup email:
          </Typography>
          <Chip label={client.backup_email} color="success" size="small" />
          <IconButton size="small" onClick={beginEdit} aria-label="Edit backup email">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleSave(null)} disabled={isLoading} aria-label="Clear backup email">
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {client.backup_email_set_by && client.backup_email_set_at && !editing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Added by {client.backup_email_set_by} on {formatDate(client.backup_email_set_at)}
        </Typography>
      )}
      {hasServmanEmail && !editing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
          Backup ignored — Servman has a company email.
        </Typography>
      )}
    </Box>
  );
};

export default CollectionClientBackupEmailCard;
